import type { LoadableData } from "@/shared/lib/loadable";
import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import type React from "react";
import type { AdminCargoListPageProps } from "../../model";
import { type CargoTableData, mapDraftOfferCargos } from "./utils";

export type CargoTableProps = {
  draftOfferCargos: LoadableData<AdminCargoListPageProps["draftOfferCargos"]>;
  onSelectOfferCargo: AdminCargoListPageProps["onSelectOfferCargo"];
};
export const CargoTable: React.FC<CargoTableProps> = (props) => {
  const data = mapDraftOfferCargos(props.draftOfferCargos.data);

  const handleRowClick = (row: CargoTableData) => {
    const selectedCargo = props.draftOfferCargos.data.find(
      (cargo) => cargo.id === row.id,
    );
    if (selectedCargo) {
      props.onSelectOfferCargo(selectedCargo);
    }
  };
  const columns: Column<CargoTableData>[] = [
    {
      key: "companyName",
      label: "荷主名",
      cell: (row) => <Typography>{row.companyName}</Typography>,
    },
    {
      key: "startAt",
      label: "積日時/積地",
      sortable: true,
      filterable: true,
      cell: (row) => {
        return (
          <div>
            <Typography fontWeight={700} color={grey[900]}>
              {row.departure}
            </Typography>
            {row.startAt ? <Typography>{row.startAt}</Typography> : null}
          </div>
        );
      },
    },
    {
      key: "endAt",
      label: "卸日時/卸地",
      sortable: true,
      filterable: true,
      cell: (row) => {
        return (
          <div>
            <Typography fontWeight={700} color={grey[900]}>
              {row.destination}
            </Typography>
            <Typography>{row.endAt}</Typography>
          </div>
        );
      },
    },
    {
      key: "transitCount",
      label: "経由地",
      align: "center",
      cell: (row) => {
        const transitCount = row.transitCount;
        if (typeof transitCount !== "number") {
          console.error(`Invalid transitCount: ${transitCount}`);
          return <>ー</>;
        }
        if (transitCount > 0) {
          return (
            <Typography fontWeight={700} fontSize={18}>
              {transitCount}
            </Typography>
          );
        }
        return <>ー</>;
      },
    },
    {
      key: "vehicle",
      label: "車両",
      cell: (row) => {
        return <Typography>{row.vehicle}</Typography>;
      },
    },
    {
      key: "shipperReceptionStatus",
      label: "受付状況(荷主/車主)",
      filterable: true,
      cell: (row) => {
        if (
          row.shipperReceptionStatus.status === "loading" ||
          row.carrierReceptionStatus.status === "loading"
        ) {
          return <CircularProgress size={16} />;
        }
        if (
          !row.shipperReceptionStatus.data ||
          !row.carrierReceptionStatus.data
        ) {
          return <>ー</>;
        }
        return (
          <Stack direction="row" spacing={0.5}>
            <OfferCargoStatusBadge
              statusType="offer_cargo_process_reception"
              status={row.shipperReceptionStatus.data}
            />
            <Typography>/</Typography>
            <OfferCargoStatusBadge
              statusType="offer_cargo_process_reception"
              status={row.carrierReceptionStatus.data}
            />
          </Stack>
        );
      },
    },
    {
      key: "expiration",
      label: "受付期限",
      sortable: true,
      cell: (row) => {
        const expiration = row.expiration;
        return (
          <Typography fontWeight={700} color={grey[900]}>
            {expiration}
          </Typography>
        );
      },
    },
    {
      key: "proposalCount",
      label: "提案件数",
      align: "center",
      cell: (row) => {
        const proposalCount = row.proposalCount;
        if (proposalCount.status === "loading") {
          return <CircularProgress size={16} />;
        }
        if (proposalCount.status === "error") {
          return <>ー</>;
        }
        return (
          <Typography fontWeight={700} fontSize={18}>
            {proposalCount.data}
          </Typography>
        );
      },
    },
  ];

  return (
    <BaseTable
      rows={data}
      columns={columns}
      onRowClick={handleRowClick}
      noDataMessage="荷物が登録されていません。"
    />
  );
};
